import { getTokenUserId } from '@utils/token/get-token-user-id';

import { type IsImpersonatingArgs } from './is-impersonating.type';

/**
 * Whether the session is acting as somebody else.
 *
 * `changeUser` stores the admin's own id and parks the admin tokens for the return trip; the
 * impersonated user's tokens go in the normal keys. The question is not "are parked tokens present"
 * — nothing expires them from storage — but "does the live session belong to someone other than the
 * admin who parked".
 *
 * The stored admin id is the reliable side of that comparison. Decoding the parked ACCESS token is
 * undecidable across a browser restart: its cookie is 14 minutes and its sessionStorage mirror is
 * wiped on close, while the parked refresh lingers for 90 days — so the parked pair reads as
 * "refresh present, access gone", which the fallback below cannot tell from a real impersonation. A
 * stored id has no such asymmetry.
 *
 * Fallback for a session parked before this shipped, or where storage refused the write: compare the
 * parked tokens, failing safe to "impersonating" when an id cannot be read, so a token that does not
 * carry `id` leaves behaviour unchanged rather than silently ending every impersonation. The parked
 * refresh cannot stand in for the missing access here — it is opaque, carrying no `id` — so a session
 * stuck before this shipped and since restarted has no on-device datum to compare and stays put until
 * one sign-out clears the parked keys.
 */
export const isImpersonating = ({ impersonatorUserId, parked, current }: IsImpersonatingArgs): boolean => {
  const currentUserId = getTokenUserId(current.accessToken);

  if (impersonatorUserId) return currentUserId !== impersonatorUserId;

  if (!parked.accessToken && !parked.refreshToken) return false;
  const parkedUserId = getTokenUserId(parked.accessToken);
  if (!parkedUserId || !currentUserId) return true;
  return parkedUserId !== currentUserId;
};
